import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showPinnedPopover } from '@rsos/components/capstone/common/Map/useDeckGL/MapPopover/mapPopoverSlice';
import useWhichReverseGeocodeService from '@rsos/hooks/useWhichReverseGeocodeService';
import { reverseGeocodeLocationToAddress } from '@rsos/maps/interface/reverseGeocode/helpers';
import { selectGeocodeService } from '@rsos/reduxDataStore';
import { WORLD } from '@rsos/reduxDataStore/src/features/gisSearch/constants';
import { setSelectedLocation } from '@rsos/reduxDataStore/src/features/gisSearch/gisSearchSlice';
import {
  isValidLatitude,
  isValidLongitude,
} from '@rsos/utils/isValidCoordinates';

interface LatLongPoint {
  latitude: number;
  longitude: number;
}

interface ReverseGeocodeResponse {
  address?:
    | string
    | {
        Match_addr?: string;
        LongLabel?: string;
        ShortLabel?: string;
        PlaceName?: string;
        Address?: string;
        City?: string;
        MetroArea?: string;
        Region?: string;
        RegionAbbr?: string;
        Postal?: string;
        CntryName?: string;
      };
  rgAddressProvider?: string;
  PlaceName?: string;
  StAddr?: string;
  SubAddr?: string;
  City?: string;
  RegionAbbr?: string;
  Postal?: string;
  MetroArea?: string;
  Region?: string;
  Zone?: string;
  LongLabel?: string;
  Addr_type?: string;
  Country?: string;
  addressObject?: {
    home?: string;
    street?: string;
    city?: string;
    region?: string;
    postal_code?: string;
    country?: string;
  };
}

interface SelectedLocation {
  location: { x: number; y: number };
  attributes: Record<string, string>;
}

const reverseGeocode = reverseGeocodeLocationToAddress as unknown as (args: {
  latitude: number;
  longitude: number;
  service?: unknown;
  serviceUrl?: string;
  token?: string | null;
}) => Promise<ReverseGeocodeResponse | null>;

export function parseLatLongTerm(term: string): LatLongPoint | null {
  const trimmed = term.trim();
  if (!trimmed || !trimmed.includes(',')) return null;

  const parts = trimmed.split(',').map(part => part.trim());
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null;

  if (!isValidLatitude(parts[0]) || !isValidLongitude(parts[1])) return null;

  const latitude = Number(parts[0]);
  const longitude = Number(parts[1]);

  return { latitude, longitude };
}

function toSelectedLocation(
  point: LatLongPoint,
  response: ReverseGeocodeResponse | null,
): SelectedLocation {
  const nestedAddress =
    response?.address && typeof response.address === 'object'
      ? response.address
      : {};
  const addressObject = response?.addressObject ?? {};
  const responseAddressLabel =
    typeof response?.address === 'string' ? response.address : '';
  const label =
    response?.LongLabel ||
    nestedAddress.LongLabel ||
    responseAddressLabel ||
    `${point.latitude}, ${point.longitude}`;
  const provider = response?.rgAddressProvider?.toLowerCase();
  const locName = provider === 'gis' ? 'Local' : WORLD;
  const placeName =
    response?.PlaceName || nestedAddress.PlaceName || addressObject.home || '';
  const streetAddress =
    response?.StAddr ||
    nestedAddress.Address ||
    nestedAddress.ShortLabel ||
    addressObject.street ||
    addressObject.home ||
    '';
  const city = response?.City || nestedAddress.City || addressObject.city || '';
  const region =
    response?.Region ||
    nestedAddress.Region ||
    nestedAddress.RegionAbbr ||
    addressObject.region ||
    '';
  const postal =
    response?.Postal || nestedAddress.Postal || addressObject.postal_code || '';

  return {
    location: { x: point.longitude, y: point.latitude },
    attributes: {
      PlaceName: placeName,
      StAddr: streetAddress,
      SubAddr: response?.SubAddr || '',
      City: city,
      RegionAbbr:
        response?.RegionAbbr ||
        nestedAddress.RegionAbbr ||
        addressObject.region ||
        region,
      Postal: postal,
      MetroArea: response?.MetroArea || nestedAddress.MetroArea || city,
      Region: region,
      Zone: response?.Zone || '',
      LongLabel: label,
      Addr_type: response?.Addr_type || 'reverse_geocode',
      Loc_name: locName,
      Country:
        response?.Country ||
        nestedAddress.CntryName ||
        addressObject.country ||
        '',
    },
  };
}

interface UseLatLongSearch {
  parse: (term: string) => LatLongPoint | null;
  search: (point: LatLongPoint) => Promise<void>;
}

export default function useLatLongSearch(): UseLatLongSearch {
  const dispatch = useDispatch();
  const service = useWhichReverseGeocodeService();
  const geocodeService = useSelector(selectGeocodeService) as
    | {
        gis_service_url?: string;
        token?: string | null;
      }
    | undefined;

  const search = useCallback(
    async (point: LatLongPoint) => {
      try {
        const response = await reverseGeocode({
          latitude: point.latitude,
          longitude: point.longitude,
          service,
          serviceUrl: geocodeService?.gis_service_url,
          token: geocodeService?.token || null,
        });
        const loc = toSelectedLocation(point, response);
        dispatch(setSelectedLocation(loc));
        dispatch(showPinnedPopover({ loc }));
      } catch {
        const loc = toSelectedLocation(point, null);
        dispatch(setSelectedLocation(loc));
        dispatch(showPinnedPopover({ loc }));
      }
    },
    [dispatch, service, geocodeService],
  );

  return useMemo(() => ({ parse: parseLatLongTerm, search }), [search]);
}
